/**
 * Where to put the reader back once the Backlog approval returns.
 *
 * Backlog comes back to one address and only one: a Nulab application
 * registers a single redirect URI, so the callback always lands on the
 * settings screen whichever screen started the trip. That used to mean the
 * approval left the reader on a page they never asked for — so the screen they
 * came from is written down on the way out and read back on the way in.
 *
 * `sessionStorage` rather than the OAuth `state`: the whole trip is one tab
 * leaving and coming back, which is exactly what per-tab storage is for, and
 * keeping it in the browser means the server never has to be trusted with — or
 * made to validate — a redirect target.
 */

const KEY = 'backlog.returnTo'

/**
 * Whether a stored value is safe to navigate to.
 *
 * In-app paths only. A value that could name another origin would turn this
 * into an open redirect: the trip through Backlog is exactly the moment a
 * reader is least likely to notice the address bar changing.
 *
 * `//evil.test` is the case worth naming — it starts with a slash and is a
 * protocol-relative URL, so a check for a leading `/` alone lets it through.
 */
export function isSafeReturnPath(value: string): boolean {
  return value.startsWith('/') && !value.startsWith('//') && !value.includes('\\')
}

/**
 * Remember the screen the approval was begun from.
 *
 * Silent when storage is unavailable — a private window with site data blocked
 * still has to be able to connect, and landing on settings is the behaviour
 * this replaced rather than a failure.
 *
 * @param path Usually `window.location.pathname + window.location.search`.
 */
export function rememberReturnPath(path: string): void {
  if (!isSafeReturnPath(path)) return
  try {
    sessionStorage.setItem(KEY, path)
  } catch {
    // Ignored on purpose: see above.
  }
}

/**
 * The remembered screen, cleared as it is read.
 *
 * Cleared either way, including when it fails the safety check: a value that
 * cannot be used is a value that should not sit there waiting for the next
 * approval to pick it up.
 *
 * @returns The path, or null when there is nothing safe to go back to.
 */
export function takeReturnPath(): string | null {
  try {
    const stored = sessionStorage.getItem(KEY)
    sessionStorage.removeItem(KEY)
    return stored && isSafeReturnPath(stored) ? stored : null
  } catch {
    return null
  }
}
